import { Box, Checkbox, Divider, Flex, Group, Modal, ScrollArea, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useApi } from 'api/api-context';
import P2Regular from 'components/typography/P2Regular';
import { cloneDeep, groupBy, isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import panic from 'errors/panic';
import { DeviceRevisionGetOptionsResponse } from 'api/actions/device-revision-get-options/device-revision-get-options-response';
import P1Medium from 'components/typography/P1Medium';
import { getDeviceType } from 'model/DeviceType';
import { RevisionDevice } from 'components/forms/revision/RevisionForm';
import DeviceCheckboxLabel from 'components/modals/pick-devices-modal/DeviceCheckboxLabel';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import Button from 'components/Button';

type Device = DeviceRevisionGetOptionsResponse[number];

export interface PickDevicesModalFormValues {
  pickedDevices: {
    [deviceId: number]: RevisionDevice;
  };
}

interface PickDevicesModalParams {
  title: string;
  alreadyPickedDeviceIds: number[];
  opened: boolean;
  revisionDeviceTypeId: number;
  departmentId: number;
  onClose: () => void;
  onPick: (value: PickDevicesModalFormValues) => void;
}

/**
 * Modal for selecting devices.
 */
export default function PickDevicesModal({
  title,
  opened,
  alreadyPickedDeviceIds,
  revisionDeviceTypeId,
  departmentId,
  onClose,
  onPick,
}: PickDevicesModalParams) {
  const { isTablet } = useResponsiveDesign();
  const [devices, setDevices] = useState<Device[]>([]);
  const [groupedDevices, setGroupedDevices] = useState<{ [deviceSubtypeId: number]: Device[] }>({});
  const revisionDeviceTypeName = useMemo(() => getDeviceType(revisionDeviceTypeId)?.name ?? '', [revisionDeviceTypeId]);

  const { getAction } = useApi();

  const form = useForm<PickDevicesModalFormValues>({
    initialValues: {
      pickedDevices: {},
    },
  });

  const pickedParent = useMemo(
    () => Object.values(form.values.pickedDevices).find((device) => device.checked && device.structure === 'parent'),
    [form.values.pickedDevices]
  );

  const pickedIds = useMemo(
    () =>
      Object.keys(form.values.pickedDevices)
        .filter((id) => form.values.pickedDevices[Number(id)].checked)
        .map(Number),
    [form.values.pickedDevices]
  );

  /**
   * Submits the form.
   */
  function submit({ pickedDevices }: PickDevicesModalFormValues) {
    const picked = cloneDeep(pickedDevices);

    if (pickedParent) {
      for (const device of Object.values(picked)) {
        if (device.parentDevice?.deviceId === pickedParent.deviceId) {
          device.checked = true;
        }
      }
    }

    onPick({ pickedDevices: picked });
    onClose();
  }

  useEffect(() => {
    const revisionDevicesGetListAction = getAction('DeviceRevisionGetOptions');

    revisionDevicesGetListAction({
      parameters: { departmentId: String(departmentId), deviceTypeId: String(revisionDeviceTypeId) },
    })
      .then((devices) => {
        const selectableDevices = devices.filter((device) => !alreadyPickedDeviceIds.includes(device.deviceId));

        const formDevices = selectableDevices.reduce((deviceMap, device) => {
          deviceMap[device.deviceId] = {
            ...device,
            termId: '',
            checked: false,
            revisionPlan: (device.revisionPlan || []).map((revisionPlan) => ({
              ...revisionPlan,
              revisionPlanId: revisionPlan.revisionPlanId!,
              deviceTypeId: revisionPlan.deviceTypeId || device.deviceTypeId,
            })),
          } satisfies RevisionDevice;

          return deviceMap;
        }, {} as { [deviceId: number]: RevisionDevice });

        setDevices(selectableDevices);
        setGroupedDevices(groupBy(selectableDevices, 'deviceSubtypeId'));
        form.setFieldValue('pickedDevices', formDevices);
      })
      .catch(panic);
  }, [getAction, departmentId, revisionDeviceTypeId, alreadyPickedDeviceIds]);

  return (
    <Modal opened={opened} onClose={onClose} title={title} centered>
      <Divider color="gray.1" mb={24} />
      <form onSubmit={form.onSubmit(submit)}>
        <ScrollArea h={410}>
          <Stack spacing={24} pb={24}>
            {Object.values(groupedDevices).map((devices) => (
              <Stack spacing={16} key={`group-${devices[0].deviceSubtypeId}`}>
                <P1Medium color="gray.9">{devices[0].deviceSubtypeName}</P1Medium>
                <Flex gap={24}>
                  <Box w={4} bg="gray.1" />
                  <Stack w="100%" spacing={16}>
                    {devices.map((device) => (
                      <Checkbox
                        key={`device-checkbox-${device.deviceId}`}
                        disabled={
                          pickedParent
                            ? pickedParent.deviceId !== device.deviceId
                            : (pickedIds.length > 0 || alreadyPickedDeviceIds.length > 0) &&
                              device.structure === 'parent'
                        }
                        label={
                          <DeviceCheckboxLabel
                            device={device}
                            revisionDeviceTypeId={revisionDeviceTypeId}
                            revisionDeviceTypeName={revisionDeviceTypeName}
                          />
                        }
                        size="lg"
                        form="pick-devices-modal"
                        name={`objekto-pickedDevices.${device.deviceId}.checked`}
                        {...form.getInputProps(`pickedDevices.${device.deviceId}.checked`, { type: 'checkbox' })}
                      />
                    ))}
                  </Stack>
                </Flex>
              </Stack>
            ))}
          </Stack>
          {isEmpty(devices) && <P2Regular>Neboli nájdené žiadne zariadenia.</P2Regular>}
        </ScrollArea>

        <Group spacing={isTablet ? 20 : 40} position="right" mt={24}>
          <Button variant="subtle-gray" type="button" onClick={onClose}>
            Zrušiť
          </Button>
          <Button variant="primary" size="md" type="submit" disabled={isEmpty(pickedIds)}>
            Pridať
          </Button>
        </Group>
      </form>
    </Modal>
  );
}
